import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AppTheme, Project, HealthRAG, MethodologyMode } from '../types';
import { listProjects, createProject, getProjectsAveragePercentComplete } from '../services/projectService';
import { ResourcePool, getTenantPool } from '../services/resourcePoolService';
import { getTenantConfigCached } from '../services/tenantConfigService';
import { getAccount } from '../auth/authService';
import { searchGroups, createGroupWithTeam, M365Group } from '../services/groupService';
import DagrunIcon from '../components/DagrunIcon';
import { BM_TITLE_INPUT } from '../components/BoardModal';

type GroupMode = 'skip' | 'existing' | 'new';

type ModuleKey =
  | 'issuesRisks'        // single toggle sets both module_issues + module_risks
  | 'milestones'
  | 'timetracking'
  | 'budget'
  | 'resources'
  | 'changemanagement'
  | 'meetingintel'
  | 'clientportal'
  | 'ai'
  | 'agile'
  | 'goals'
  | 'training'
  | 'punchlist'
  | 'stakeholders'
  | 'submittalrfi'
  | 'lessons';

interface ModuleDef { key: ModuleKey; label: string; desc: string; default: boolean; }

const MODULES: ModuleDef[] = [
  { key: 'issuesRisks',      label: 'Issues & Risks',       desc: 'Track blockers, risks, and open issues',            default: true  },
  { key: 'milestones',       label: 'Milestones & Phases',  desc: 'Mark key deliverables and project phases',          default: true  },
  { key: 'timetracking',     label: 'Time Tracking',        desc: 'Log hours and track effort per task',               default: false },
  { key: 'budget',           label: 'Budget & Expenses',    desc: 'Manage project spend and expenses',                 default: false },
  { key: 'resources',        label: 'Resource Management',  desc: 'View team capacity and workload',                   default: false },
  { key: 'changemanagement', label: 'Change Management',    desc: 'Manage change requests and approvals',              default: false },
  { key: 'meetingintel',     label: 'Meeting Intelligence', desc: 'Capture action items from Teams meetings',          default: false },
  { key: 'clientportal',     label: 'Client Portal',        desc: 'Share project status with external clients',        default: false },
  { key: 'ai',               label: 'AI Project Manager',   desc: 'AI-assisted task suggestions and scheduling',       default: false },
  { key: 'agile',            label: 'Agile / Sprint Mode',  desc: 'Run sprints with backlog and burndown',             default: true  },
  { key: 'goals',            label: 'Goals',                desc: 'Track measurable project objectives',               default: false },
  { key: 'training',         label: 'Training Records',     desc: 'Track training completion by team member. Assign training items, record scores and attestations, export compliance reports.', default: false },
  { key: 'punchlist',        label: 'Punch List',           desc: 'Manage close-out deficiencies. Track items by location, trade, and status. Generate PDF punch list reports for field distribution.', default: false },
  { key: 'stakeholders',     label: 'Stakeholder Register', desc: 'Track external stakeholders, their influence, sentiment, and engagement history. Includes influence/interest matrix view.', default: false },
  { key: 'submittalrfi',     label: 'Construction Document Control', desc: 'Manage submittals and RFIs with auto-numbering, review cycles, and PDF export. Links to SharePoint for drawing references.', default: false },
  { key: 'lessons',          label: 'Lessons Learned',      desc: 'Post-mortem log of issues, impacts, and recommendations', default: false },
];

const METHODOLOGY_OPTIONS: { value: number; label: string }[] = [
  { value: MethodologyMode.Waterfall, label: 'Waterfall' },
  { value: MethodologyMode.Agile,     label: 'Agile' },
  { value: MethodologyMode.Kanban,    label: 'Kanban' },
  { value: MethodologyMode.Hybrid,    label: 'Hybrid' },
];

const sectionLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, letterSpacing: '0.22em',
  textTransform: 'uppercase', color: 'var(--ink-4)',
  fontFamily: 'var(--font-ui)',
  marginTop: 4, marginBottom: 10,
};
const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 12, color: 'var(--ink-3)', marginBottom: 4,
  fontFamily: 'var(--font-ui)',
};

interface Props { pca: IPublicClientApplication; theme: AppTheme; }

const STATUS_LABEL: Record<number, string> = {
  100000000: 'Draft', 100000001: 'Active', 100000002: 'On Hold', 100000003: 'At Risk',
  100000004: 'Complete', 100000005: 'Cancelled', 100000006: 'Archived',
};
// Ordered option list for the status filter — the 7-state lifecycle + All.
const STATUS_FILTER_OPTIONS: { value: number; label: string }[] = [
  100000001, 100000000, 100000002, 100000003, 100000004, 100000005, 100000006,
].map(v => ({ value: v, label: STATUS_LABEL[v] }));
const PROJECT_STATUS_ACTIVE = 100000001;
const STATUS_FILTER_STORAGE = 'dagrun_pm_projects_status_filter';
const HEALTH_COLOR: Record<number, string> = {
  [HealthRAG.Green]: 'var(--success)',
  [HealthRAG.Amber]: 'var(--warning)',
  [HealthRAG.Red]:   'var(--danger)',
};

export default function Projects({ pca }: Props) {
  const nav = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [showNew, setShowNew]         = useState(false);
  const [newName, setNewName]         = useState('');
  const [newDesc, setNewDesc]         = useState('');
  const [newMethodology, setNewMethodology] = useState<number>(MethodologyMode.Kanban);
  const [modules, setModules] = useState<Record<ModuleKey, boolean>>(() => {
    const init: Record<string, boolean> = {};
    MODULES.forEach(m => { init[m.key] = m.default; });
    return init as Record<ModuleKey, boolean>;
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [rfpEnabled, setRfpEnabled] = useState(false);
  // Resource Pool wizard step — off by default. The tenant pool is loaded
  // lazily when the modal opens so the step can distinguish "pool exists"
  // from "needs Tenant Admin setup first". undefined = still loading.
  const [usePool, setUsePool] = useState(false);
  const [tenantPool, setTenantPool] = useState<ResourcePool | null | undefined>(undefined);
  // Optional M365 Group linkage step. Default 'skip' (Skip is never removed).
  const [groupMode, setGroupMode] = useState<GroupMode>('skip');
  const [groupSearch, setGroupSearch] = useState('');
  const [groupResults, setGroupResults] = useState<M365Group[]>([]);
  const [groupSearching, setGroupSearching] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<M365Group | null>(null);
  // Status filter — defaults to Active; persisted so the choice survives
  // navigation and reloads.
  const [statusFilter, setStatusFilter] = useState<number | 'all'>(() => {
    try {
      const raw = window.localStorage.getItem(STATUS_FILTER_STORAGE);
      if (raw === 'all') return 'all';
      const n = Number(raw);
      if (raw !== null && STATUS_LABEL[n]) return n;
    } catch { /* ignore */ }
    return PROJECT_STATUS_ACTIVE;
  });
  const pickStatusFilter = (v: number | 'all') => {
    setStatusFilter(v);
    try { window.localStorage.setItem(STATUS_FILTER_STORAGE, String(v)); } catch { /* ignore */ }
  };

  // Surface the RFP Pipeline button in the toolbar only when the
  // tenant admin has flipped dagrun_pm_rfpenabled on. Failure to read
  // the config (e.g. table missing in older envs) is treated as off.
  useEffect(() => {
    let cancelled = false;
    getTenantConfigCached(pca)
      .then(cfg => { if (!cancelled) setRfpEnabled(!!cfg.dagrun_pm_rfpenabled); })
      .catch(() => { if (!cancelled) setRfpEnabled(false); });
    return () => { cancelled = true; };
  }, [pca]);

  useEffect(() => {
    if (!showNew) return;
    let cancelled = false;
    getTenantPool(pca)
      .then(p => { if (!cancelled) setTenantPool(p); })
      .catch(() => { if (!cancelled) setTenantPool(null); });
    return () => { cancelled = true; };
  }, [pca, showNew]);

  const resetForm = () => {
    setNewName(''); setNewDesc(''); setNewMethodology(MethodologyMode.Kanban);
    setUsePool(false);
    setGroupMode('skip'); setGroupSearch(''); setGroupResults([]); setSelectedGroup(null);
    const init: Record<string, boolean> = {};
    MODULES.forEach(m => { init[m.key] = m.default; });
    setModules(init as Record<ModuleKey, boolean>);
    setCreateError(null);
  };

  // Debounced group search for the "Link existing" mode.
  useEffect(() => {
    if (groupMode !== 'existing' || groupSearch.trim().length < 2) { setGroupResults([]); return; }
    let cancelled = false;
    setGroupSearching(true);
    const t = window.setTimeout(async () => {
      try { const r = await searchGroups(pca, groupSearch.trim()); if (!cancelled) setGroupResults(r); }
      catch { if (!cancelled) setGroupResults([]); }
      finally { if (!cancelled) setGroupSearching(false); }
    }, 300);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [pca, groupMode, groupSearch]);

  const closeModal = () => { setShowNew(false); resetForm(); };

  const reload = async () => {
    try {
      setLoading(true);
      const [list, avgMap] = await Promise.all([
        listProjects(pca),
        getProjectsAveragePercentComplete(pca),
      ]);
      // Overlay computed averages onto the project list so the progress bar
      // reflects task-derived completion rather than the stored project field.
      setProjects(list.map(p => ({
        ...p,
        dagrun_pm_percentcomplete: avgMap.get(p.dagrun_pm_projectid) ?? 0,
      })));
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      // Creator identity from MSAL — real AAD OID, never a name/email match.
      const acct = await getAccount(pca);
      const creator = acct
        ? { userId: acct.localAccountId, userName: acct.name ?? acct.username ?? 'Me', userEmail: acct.username ?? null }
        : undefined;

      // Resolve the optional M365 Group linkage BEFORE creating the project so
      // its id can be stamped on the project row. 'new' provisions Group-first
      // (POST /groups → PUT team) via the existing helper; 'existing' uses the
      // picked group; 'skip' links nothing.
      let m365GroupId: string | null = null;
      if (groupMode === 'existing' && selectedGroup) m365GroupId = selectedGroup.id;
      else if (groupMode === 'new') m365GroupId = await createGroupWithTeam(pca, newName.trim());

      // Issues & Risks is a single toggle that sets both module flags.
      const ir = modules.issuesRisks;
      const id = await createProject(pca, {
        name: newName.trim(),
        description: newDesc.trim() || null,
        methodology: newMethodology,
        creator,
        m365GroupId,
        useGlobalResourcePool: usePool && !!tenantPool,
        resourcePoolId: usePool && tenantPool ? tenantPool.dagrun_pm_resourcepoolid : null,
        modules: {
          issues:           ir,
          risks:            ir,
          milestones:       modules.milestones,
          timetracking:     modules.timetracking,
          budget:           modules.budget,
          resources:        modules.resources,
          changemanagement: modules.changemanagement,
          meetingintel:     modules.meetingintel,
          clientportal:     modules.clientportal,
          ai:               modules.ai,
          agile:            modules.agile,
          goals:            modules.goals,
          training:         modules.training,
          punchlist:        modules.punchlist,
          stakeholders:     modules.stakeholders,
          submittalrfi:     modules.submittalrfi,
          lessons:          modules.lessons,
        },
      });
      setShowNew(false); resetForm();
      nav(`/project/${id}`);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Create failed');
    } finally { setCreating(false); }
  };

  const filteredProjects = statusFilter === 'all'
    ? projects
    : projects.filter(p => p.dagrun_pm_lifecyclestatus === statusFilter);

  return (
    <div className="page">
      <div className="page-header">
        <h2>Projects</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={String(statusFilter)}
            onChange={e => pickStatusFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            title="Filter by project status"
            style={{
              fontFamily: 'var(--font-ui)', fontSize: 13,
              padding: '7px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              background: '#fff', color: 'var(--ink)',
              cursor: 'pointer',
            }}
          >
            {STATUS_FILTER_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
            <option value="all">All statuses</option>
          </select>
          {rfpEnabled && (
            <button type="button" onClick={() => nav('/rfp')}
              style={{ background: 'transparent', border: '1px solid #0E7490', color: '#0E7490', padding: '7px 14px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#D4EEF5')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >RFP Pipeline</button>
          )}
          <button type="button" onClick={() => nav('/portfolio/timeline')}
            style={{ background: 'transparent', border: '1px solid #0E7490', color: '#0E7490', padding: '7px 14px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#D4EEF5')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >Timeline</button>
          <button className="btn-primary" onClick={() => setShowNew(true)}>+ New Project</button>
        </div>
      </div>

      {loading && <div className="spinner" style={{ margin: '80px auto' }} />}
      {error && <div className="card" style={{ padding: 20, color: 'var(--danger)', fontSize: 14 }}>{error}</div>}

      {!loading && !error && projects.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div style={{
            width: 64, height: 64, background: 'var(--brand)',
            borderRadius: 'var(--r-xl)', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
            boxShadow: 'var(--shadow-brand)',
          }}>
            <DagrunIcon size={40} />
          </div>
          <h3 style={{
            fontFamily: 'var(--font-display)', fontWeight: 300,
            fontSize: 22, color: 'var(--ink-2)', marginTop: 20,
          }}>No projects yet</h3>
          <p style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 8 }}>
            Create your first project to get started.
          </p>
          <button className="btn-primary" style={{ marginTop: 24 }} onClick={() => setShowNew(true)}>
            + New Project
          </button>
        </div>
      )}

      {!loading && !error && projects.length > 0 && filteredProjects.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--ink-3)', fontSize: 13 }}>
          No {statusFilter === 'all' ? '' : `${STATUS_LABEL[statusFilter]} `}projects.
          {' '}
          <button
            type="button"
            onClick={() => pickStatusFilter('all')}
            style={{ background: 'transparent', border: 'none', color: 'var(--brand)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)', padding: 0 }}
          >Show all statuses</button>
        </div>
      )}

      {!loading && filteredProjects.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {filteredProjects.map(p => (
            <div
              key={p.dagrun_pm_projectid}
              className="card"
              onClick={() => nav(`/project/${p.dagrun_pm_projectid}`)}
              style={{
                padding: 16, cursor: 'pointer', position: 'relative',
                borderTop: '3px solid var(--brand)',
                transition: 'box-shadow 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <strong style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                  {p.dagrun_pm_name}
                </strong>
                {p.dagrun_pm_healthrag != null && (
                  <span title="Health" style={{
                    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                    background: HEALTH_COLOR[p.dagrun_pm_healthrag] ?? 'var(--ink-4)',
                    alignSelf: 'center',
                  }} />
                )}
              </div>
              {p.dagrun_pm_description && (
                <p style={{
                  fontSize: 12, color: 'var(--ink-3)',
                  lineHeight: 1.45, marginBottom: 12,
                  display: '-webkit-box',
                  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {p.dagrun_pm_description}
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                <span className="pill">{STATUS_LABEL[p.dagrun_pm_lifecyclestatus] ?? '—'}</span>
                <span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
                  {p.dagrun_pm_percentcomplete ?? 0}% complete
                </span>
              </div>
              <div style={{ background: 'var(--surface-2)', borderRadius: 3, height: 4, overflow: 'hidden', marginTop: 8 }}>
                <div style={{ height: '100%', width: `${p.dagrun_pm_percentcomplete ?? 0}%`, background: 'var(--brand)' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <div className="modal-overlay" onClick={closeModal} style={{ background: 'rgba(15,17,23,0.18)' }}>
          <div
            className="modal-panel"
            onClick={e => e.stopPropagation()}
            style={{ width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', padding: 24 }}
          >
            <h3 style={{ margin: '0 0 20px 0', fontSize: 18 }}>New Project</h3>

            {/* Section 1 — Basics */}
            <div style={sectionLabel}>Project Basics</div>
            <div style={{ marginBottom: 14 }}>
              <label style={fieldLabel}>Project Name *</label>
              <input
                autoFocus
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') closeModal(); }}
                placeholder="e.g. Q2 Brand Refresh"
                style={BM_TITLE_INPUT}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={fieldLabel}>Description</label>
              <textarea
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                rows={2}
                placeholder="Optional one-liner describing the project"
                style={{ minHeight: 54 }}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={fieldLabel}>Methodology</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {METHODOLOGY_OPTIONS.map(opt => {
                  const active = newMethodology === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setNewMethodology(opt.value)}
                      style={{
                        flex: 1,
                        padding: '7px 12px',
                        background: active ? 'var(--brand-mist)' : 'var(--surface)',
                        color:      active ? 'var(--brand)'     : 'var(--ink-3)',
                        border: `1px solid ${active ? 'var(--brand)' : 'var(--border)'}`,
                        borderRadius: 'var(--r-sm)',
                        fontFamily: 'var(--font-ui)',
                        fontSize: 13,
                        fontWeight: active ? 500 : 400,
                        cursor: 'pointer',
                        transition: 'all 0.12s',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Section 2 — Modules */}
            <div style={sectionLabel}>Modules</div>
            <p style={{ fontSize: 12, color: 'var(--ink-4)', margin: '0 0 10px 0', lineHeight: 1.45 }}>
              Enable the features your team needs. You can change these any time in Project Settings.
            </p>
            <div style={{ border: '1px solid var(--border-light)', borderRadius: 'var(--r-md)', padding: '6px 10px' }}>
              {MODULES.map(m => (
                <label
                  key={m.key}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '8px 0',
                    borderBottom: '0.5px solid var(--border-light)',
                    cursor: 'pointer',
                    fontWeight: 400,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={modules[m.key]}
                    onChange={e => setModules(prev => ({ ...prev, [m.key]: e.target.checked }))}
                    style={{ marginTop: 2, width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>{m.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 1 }}>{m.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            {/* Section 3 — Resource Pool */}
            <div style={{ ...sectionLabel, marginTop: 18 }}>Resource Pool</div>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '10px 12px',
              border: '1px solid var(--border-light)', borderRadius: 'var(--r-md)',
              background: usePool ? 'var(--brand-mist)' : 'var(--surface)',
              cursor: 'pointer', fontWeight: 400,
              transition: 'background 0.12s',
            }}>
              <input
                type="checkbox"
                checked={usePool}
                onChange={e => setUsePool(e.target.checked)}
                style={{ marginTop: 2, width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>Use the global resource pool</div>
                <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 1 }}>
                  Pool members become available to this project with their default rates and
                  hours. Per-project overrides always win.
                </div>
              </div>
            </label>
            {usePool && tenantPool === null && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-2)', padding: '8px 12px', background: '#FEF3C7', borderRadius: 6 }}>
                No resource pool exists for this tenant yet. Configure one in{' '}
                <button
                  type="button"
                  onClick={() => nav('/admin')}
                  style={{ background: 'transparent', border: 'none', color: 'var(--brand)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)', padding: 0 }}
                >Tenant Admin</button>
                {' '}first — the project will be created without pool linkage.
              </div>
            )}

            {/* Section 4 — Microsoft 365 Group (optional; Skip is the default) */}
            <div style={{ ...sectionLabel, marginTop: 18 }}>Microsoft 365 Group</div>
            <p style={{ fontSize: 12, color: 'var(--ink-4)', margin: '0 0 8px 0', lineHeight: 1.45 }}>
              Optionally back this project with a Microsoft 365 Group + Team. You can also do this
              later in Project Settings.
            </p>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {([['skip', 'Skip'], ['existing', 'Link existing'], ['new', 'Create new']] as [GroupMode, string][]).map(([mode, label]) => {
                const active = groupMode === mode;
                return (
                  <button key={mode} type="button"
                    onClick={() => { setGroupMode(mode); setSelectedGroup(null); setGroupSearch(''); setGroupResults([]); }}
                    style={{
                      flex: 1, padding: '7px 12px', borderRadius: 'var(--r-sm)', fontSize: 13, cursor: 'pointer',
                      fontFamily: 'var(--font-ui)', fontWeight: active ? 500 : 400,
                      background: active ? 'var(--brand-mist)' : 'var(--surface)',
                      color: active ? 'var(--brand)' : 'var(--ink-3)',
                      border: `1px solid ${active ? 'var(--brand)' : 'var(--border)'}`,
                    }}>{label}</button>
                );
              })}
            </div>
            {groupMode === 'existing' && (
              <div style={{ marginBottom: 4 }}>
                {selectedGroup ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--border-light)', borderRadius: 'var(--r-md)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>{selectedGroup.displayName}</div>
                      {selectedGroup.mail && <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{selectedGroup.mail}</div>}
                    </div>
                    <button type="button" onClick={() => setSelectedGroup(null)}
                      style={{ background: 'transparent', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 15 }}>×</button>
                  </div>
                ) : (
                  <>
                    <input value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Search Microsoft 365 Groups…"
                      style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', boxSizing: 'border-box' }} />
                    <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 4 }}>
                      {groupSearching && <div style={{ padding: 8, fontSize: 12, color: 'var(--ink-4)' }}>Searching…</div>}
                      {groupResults.map(g => (
                        <button key={g.id} type="button" onClick={() => setSelectedGroup(g)}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderBottom: '0.5px solid var(--border-light)', background: 'transparent', cursor: 'pointer' }}>
                          <div style={{ fontSize: 13, color: 'var(--ink)' }}>{g.displayName}</div>
                          {g.mail && <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{g.mail}</div>}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {groupMode === 'new' && (
              <div style={{ fontSize: 12, color: 'var(--ink-2)', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 6 }}>
                A new Microsoft 365 Group + Team named “{newName.trim() || 'this project'}” will be created and linked when you create the project.
              </div>
            )}

            {createError && (
              <div style={{ marginTop: 14, fontSize: 12, color: 'var(--danger)' }}>{createError}</div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn-secondary" onClick={closeModal} disabled={creating}>Cancel</button>
              <button className="btn-primary" onClick={handleCreate} disabled={creating || !newName.trim()}>
                {creating ? 'Creating…' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
